import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/pro-regular-svg-icons";

import Drawer from "components/drawerSlide/drawer";
import SelectNew from "components/select/SelectNew";
import { StatusCode } from "api/protocols";
import { GetFacilityAdmin } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import { GetReport, IReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { PostScheduledReport, PutScheduledReport } from "api/rpc/2024-04/facilityAdmin/report/scheduledReport";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { IMergedReportEntry } from "pages/secure/shared/reports/reportCatalog.types";
import { DEFAULT_SCHEDULED_REPORT_TIME } from "pages/secure/shared/reports/scheduledReport.constants";
import { IUser } from "redux/reducers/models/user";

const WEEKLY_DAY_OF_WEEK_OPTIONS = [
  { id: "Monday", label: "Monday" },
  { id: "Tuesday", label: "Tuesday" },
  { id: "Wednesday", label: "Wednesday" },
  { id: "Thursday", label: "Thursday" },
  { id: "Friday", label: "Friday" },
  { id: "Saturday", label: "Saturday" },
  { id: "Sunday", label: "Sunday" },
] as const;

const DAILY_DAY_OF_WEEK = "All";

type TScheduleFrequency = "daily" | "weekly";

export interface IReportScheduleDrawerEditTarget {
  id: number | string;
  reportId: number | string;
  reportTitle: string;
  adminId: number | string;
  schedule: string;
  dayOfWeek: string;
}

interface IReportScheduleDrawerProps {
  open: boolean;
  report?: IMergedReportEntry | null;
  editTarget?: IReportScheduleDrawerEditTarget | null;
  showReportSelect?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function normalizeScheduleFrequency(schedule: string): TScheduleFrequency {
  return schedule === "weekly" ? "weekly" : "daily";
}

function normalizeDayOfWeek(schedule: TScheduleFrequency, dayOfWeek: string): string {
  if (schedule === "daily") {
    return DAILY_DAY_OF_WEEK;
  }

  return WEEKLY_DAY_OF_WEEK_OPTIONS.some(option => option.id === dayOfWeek) ? dayOfWeek : "Monday";
}

export default function ReportScheduleDrawer(props: IReportScheduleDrawerProps) {
  const { open, report = null, editTarget = null, showReportSelect = false, onClose, onSuccess } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isEditMode = editTarget != null;
  const shouldLoadReports = showReportSelect && !isEditMode;

  const [admins, setAdmins] = useState<IUser[] | undefined>(undefined);
  const [reports, setReports] = useState<IReport[] | undefined>(undefined);
  const [selectedReportId, setSelectedReportId] = useState<number | string | null>(null);
  const [adminId, setAdminId] = useState<number | string | null>(null);
  const [schedule, setSchedule] = useState<TScheduleFrequency>("daily");
  const [dayOfWeek, setDayOfWeek] = useState<string>("Monday");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    const initialSchedule = normalizeScheduleFrequency(editTarget?.schedule ?? "daily");
    const initialDayOfWeek = normalizeDayOfWeek(initialSchedule, editTarget?.dayOfWeek ?? "Monday");

    setSelectedReportId(editTarget?.reportId ?? report?.id ?? null);
    setAdminId(editTarget?.adminId ?? null);
    setSchedule(initialSchedule);
    setDayOfWeek(initialDayOfWeek);

    const source = axios.CancelToken.source();
    void loadAdmins(source.token);

    if (shouldLoadReports) {
      void loadReports(source.token);
    } else {
      setReports(undefined);
    }

    return () => source.cancel();
  }, [editTarget, open, report?.id, shouldLoadReports]);

  async function loadAdmins(token?: CancelToken) {
    const response = await GetFacilityAdmin(null, token ? false : true, token);

    if (token?.reason) {
      return;
    }

    if (response.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading staff accounts")));
      setAdmins([]);
      return;
    }

    setAdmins(response.data as unknown as IUser[]);
  }

  async function loadReports(token?: CancelToken) {
    const response = await GetReport(null, token ? false : true, token);

    if (token?.reason) {
      return;
    }

    if (response.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading reports")));
      setReports([]);
      return;
    }

    setReports(response.data);
  }

  async function handleSubmit() {
    const reportId = editTarget?.reportId ?? report?.id ?? selectedReportId;

    if (reportId == null || adminId == null) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      report_id: Number(reportId),
      admin_id: Number(adminId),
      schedule,
      day_of_week: schedule === "daily" ? DAILY_DAY_OF_WEEK : dayOfWeek,
      time: DEFAULT_SCHEDULED_REPORT_TIME,
    };

    const response = isEditMode
      ? await PutScheduledReport(
          {
            id: Number(editTarget.id),
            report_id: payload.report_id,
            admin_id: payload.admin_id,
            schedule: payload.schedule,
            day_of_week: payload.day_of_week,
          },
          true,
        )
      : await PostScheduledReport(payload, true);

    setIsSubmitting(false);

    if (response.status !== StatusCode.OK) {
      dispatch(
        showError(
          typeof response.message === "string"
            ? response.message
            : t(isEditMode ? "Error updating scheduled report" : "Error creating scheduled report"),
        ),
      );
      return;
    }

    dispatch(
      showSuccess(
        typeof response.message === "string"
          ? response.message
          : t(isEditMode ? "Scheduled report updated successfully" : "Report scheduled successfully"),
      ),
    );
    onSuccess?.();
    onClose();
  }

  const adminOptions = admins?.map(admin => ({ id: admin.id, label: admin.full_name })) ?? [];
  const reportOptions = reports?.map(reportOption => ({ id: reportOption.id, label: reportOption.title })) ?? [];
  const weeklyDayOptions = WEEKLY_DAY_OF_WEEK_OPTIONS.map(option => ({
    id: option.id,
    label: t(option.label),
  }));
  const isWeekly = schedule === "weekly";
  const resolvedReportId = editTarget?.reportId ?? report?.id ?? selectedReportId;
  const subtitle = editTarget?.reportTitle ?? (report ? t(report.title) : undefined);
  const canSubmit =
    resolvedReportId != null &&
    adminId != null &&
    !isSubmitting &&
    (!isWeekly || WEEKLY_DAY_OF_WEEK_OPTIONS.some(option => option.id === dayOfWeek));

  return (
    <Drawer
      className="report-schedule-drawer"
      open={open}
      title={t(isEditMode ? "Edit scheduled report" : "Schedule report")}
      subtitle={subtitle}
      headerLeading={
        <span className="report-schedule-drawer__header-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faClock} />
        </span>
      }
      backDropClose
      showCancel
      cancelText={t("Cancel")}
      okText={t(isEditMode ? "Update" : "Schedule report")}
      okDisabled={!canSubmit}
      onClose={onClose}
      onOk={() => void handleSubmit()}
    >
      <Drawer.Section>
        <div className="report-schedule-drawer__form">
          {shouldLoadReports ? (
            <SelectNew
              id="report_schedule_report_select"
              value={selectedReportId}
              onChange={val => setSelectedReportId(val)}
              options={reportOptions}
              label={t("Report")}
              disabled={reports === undefined}
            />
          ) : null}
          <SelectNew
            id="report_schedule_admin_select"
            value={adminId}
            onChange={val => setAdminId(val)}
            options={adminOptions}
            label={t("Send to")}
            disabled={admins === undefined}
          />
          <div className="report-schedule-drawer__frequency">
            <span className="report-schedule-drawer__frequency-label">{t("When to send")}</span>
            <div className="report-schedule-drawer__frequency-options" role="radiogroup" aria-label={t("When to send")}>
              <button
                type="button"
                role="radio"
                aria-checked={schedule === "daily"}
                className={`report-schedule-drawer__frequency-option${schedule === "daily" ? " report-schedule-drawer__frequency-option--selected" : ""}`}
                onClick={() => setSchedule("daily")}
              >
                <span className="report-schedule-drawer__frequency-radio" aria-hidden="true" />
                <span>{t("Daily")}</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={schedule === "weekly"}
                className={`report-schedule-drawer__frequency-option${schedule === "weekly" ? " report-schedule-drawer__frequency-option--selected" : ""}`}
                onClick={() => {
                  setSchedule("weekly");
                  if (!WEEKLY_DAY_OF_WEEK_OPTIONS.some(option => option.id === dayOfWeek)) {
                    setDayOfWeek("Monday");
                  }
                }}
              >
                <span className="report-schedule-drawer__frequency-radio" aria-hidden="true" />
                <span>{t("Weekly")}</span>
              </button>
            </div>
          </div>
          {isWeekly ? (
            <SelectNew
              id="report_schedule_day_select"
              value={dayOfWeek}
              onChange={val => setDayOfWeek(String(val))}
              options={weeklyDayOptions}
              label={t("Day of week")}
            />
          ) : null}
        </div>
      </Drawer.Section>
    </Drawer>
  );
}
